import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile } from "@firebase/auth";
import { useState, type FormEvent } from "react";
import { api } from "../api";
import { AuthCard, Layout } from "../components/Layout";
import { Checkbox, Field, FormError, PasswordField, SubmitButton } from "../components/Form";
import { GoogleButton } from "../components/GoogleButton";
import { config } from "../config";
import { firebaseAuth } from "../firebase";
import { errorMessage } from "../messages";
import { Link, Redirect, useRouter } from "../router";
import { homeFor, useSession } from "../session";
import { MIN_PASSWORD_LENGTH, isEmail, normalizeFullName, normalizeIsraeliPhone } from "../validation";

type Errors = Partial<Record<"fullName" | "email" | "phone" | "password" | "terms", string>>;

export function SignUp() {
  const { session, refresh } = useSession();
  const { navigate } = useRouter();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Someone already signed in has nothing to do here, unless this form is
  // the one signing them up right now.
  if (session.status === "signed-in" && !busy) {
    return <Redirect to={homeFor(session.me)} />;
  }

  const validate = (): Errors => {
    const found: Errors = {};
    if (!normalizeFullName(fullName)) found.fullName = "נדרש שם מלא: שם פרטי ושם משפחה.";
    if (!isEmail(email)) found.email = "כתובת האימייל לא תקינה.";
    if (!normalizeIsraeliPhone(phone)) found.phone = "נדרש מספר טלפון ישראלי, למשל 050-1234567.";
    if (password.length < MIN_PASSWORD_LENGTH) found.password = `הסיסמה צריכה להיות באורך ${MIN_PASSWORD_LENGTH} תווים לפחות.`;
    if (!terms) found.terms = "כדי לפתוח חשבון צריך לאשר את תנאי השימוש ואת מדיניות הפרטיות.";
    return found;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }

    setBusy(true);
    try {
      const auth = firebaseAuth();
      const { user } = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const name = normalizeFullName(fullName) as string;
      await updateProfile(user, { displayName: name });
      // Not waiting for the email: a delay there must not block the account.
      void sendEmailVerification(user, { url: `${location.origin}/account` }).catch(() => undefined);

      const token = await user.getIdToken();
      const me = await api.me(token);
      await api.saveProfile(token, { fullName: name, phone, marketingOptIn: marketing });
      await api.acceptTerms(token, me.termsVersion);
      await refresh();
      navigate("/account", { replace: true });
    } catch (error) {
      setFormError(errorMessage(error));
      // If the Firebase account exists by now, the account page finishes the
      // remaining steps, so the customer is never stuck.
      if (firebaseAuth().currentUser) {
        await refresh();
        navigate("/complete-profile", { replace: true });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <AuthCard
        title="פתיחת חשבון ב-FrameFlow"
        subtitle="מפרק פודקאסט לרילסים אנכיים, ישר על המק שלכם. ההרשמה לוקחת דקה."
      >
        <form onSubmit={(event) => void submit(event)} noValidate>
          <Field
            label="שם מלא"
            value={fullName}
            onChange={setFullName}
            autoComplete="name"
            placeholder="שם פרטי ושם משפחה"
            error={errors.fullName}
          />
          <Field
            label="אימייל"
            type="email"
            dir="ltr"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            inputMode="email"
            placeholder="name@example.com"
            error={errors.email}
          />
          <Field
            label="טלפון"
            type="tel"
            dir="ltr"
            value={phone}
            onChange={setPhone}
            autoComplete="tel"
            inputMode="tel"
            placeholder="050-1234567"
            hint="כדי שנוכל לחזור אליכם להפעלת החשבון ולתמיכה."
            error={errors.phone}
          />
          <PasswordField
            label="סיסמה"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            hint={`לפחות ${MIN_PASSWORD_LENGTH} תווים.`}
            error={errors.password}
          />
          <Checkbox checked={terms} onChange={setTerms} error={errors.terms}>
            קראתי ואני מאשר/ת את{" "}
            <a href={config.termsUrl} target="_blank" rel="noopener">
              תנאי השימוש
            </a>{" "}
            ואת{" "}
            <a href={config.privacyUrl} target="_blank" rel="noopener">
              מדיניות הפרטיות
            </a>
            .
          </Checkbox>
          <Checkbox checked={marketing} onChange={setMarketing}>
            אשמח לקבל מ-FrameFlow עדכונים, טיפים והצעות באימייל, ב-SMS ובוואטסאפ. אפשר לבטל בכל עת.
          </Checkbox>
          <FormError message={formError} />
          <SubmitButton busy={busy}>פתיחת חשבון</SubmitButton>
        </form>
        <GoogleButton onError={setFormError} />
        <p className="switch">
          כבר יש לכם חשבון? <Link to="/login">התחברות</Link>
        </p>
        <p className="fine-print">
          הפרטים משמשים לניהול החשבון, לתמיכה ולחיוב. פרטים נוספים ב
          <a href={config.privacyUrl} target="_blank" rel="noopener">
            מדיניות הפרטיות
          </a>
          .
        </p>
      </AuthCard>
    </Layout>
  );
}
